import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import {
  Button,
  FormControl,
  Input,
  InputLabel,
  Link,
} from '@material-ui/core';
import { CheckCircle } from '@material-ui/icons';
import Header from '../../components/Header';
import LinearLoader from '../../components/LinearLoader';
import Notification from '../../components/Notification';
import PatientForm from '../login/PatientForm';
import { userActionsTypes } from '../../redux/user/actions';
import { HOMEPAGE } from '../../utils/urls';
import useCheckoutPlans, { CHECKOUT_MODE } from '../../hooks/useCheckoutPlans';
import { useStyles } from './style';
import StripePaymentForm from './StripePaymentForm';
import PlanSelector from './PlanSelector';
import {
  formatMoney,
  getIntervalLabel,
  getRecurringLabel,
} from './planUtils';

const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const isOlderThan18 = (dateOfBirth) => {
  const diff = moment().diff(moment(dateOfBirth), 'years');
  return diff >= 18;
};

const getPlanInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

function Checkout({
  session,
  userState,
  onLogin,
  onSignup,
  getInitialData,
  deleteMessages,
  getMySubscription,
}) {
  const classes = useStyles();
  const history = useHistory();
  const {
    mode,
    plans,
    selectedPlan,
    loading,
    error,
    selectPlan,
    clearPlan,
    startCheckoutSession,
    checkoutLoading,
    checkoutError,
    paymentStatus,
    refetchPlans,
  } = useCheckoutPlans();

  const isAuthenticated = Boolean(session && session.email);
  const [authView, setAuthView] = useState('buttons');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [cancelNotice, setCancelNotice] = useState(false);

  const { countries } = userState;
  const intervalLabel = selectedPlan
    ? getIntervalLabel(selectedPlan.interval, selectedPlan.intervalCount)
    : '';
  const totalLabel = selectedPlan
    ? formatMoney(selectedPlan.amount, selectedPlan.currency)
    : '';

  useEffect(() => {
    if (authView === 'signup') {
      getInitialData();
    }
  }, [authView]);

  useEffect(() => {
    if (userState.success && authView === 'signup') {
      setAuthView('login');
    }
  }, [userState.success, authView]);

  useEffect(() => {
    if (paymentStatus === 'cancel') {
      setCancelNotice(true);
    }
  }, [paymentStatus]);

  useEffect(() => {
    if (paymentStatus === 'success') {
      getMySubscription();
    }
  }, [paymentStatus]);

  const handleLogin = (event) => {
    event.preventDefault();
    if (email && password) {
      onLogin({ email, password });
    }
  };

  const renderCheckoutForm = () => (
    <div className={classes.content}>
      <div className={classes.leftColumn}>
        {paymentStatus === 'success' ? (
          <div className={classes.successBox}>
            <h2 className={classes.successTitle}>Pago confirmado</h2>
            <p className={classes.successText}>
              Tu suscripción
              {selectedPlan ? ` a ${selectedPlan.productName}` : ''} quedó
              activa.
              {selectedPlan &&
                ` Se te cobrará ${totalLabel} ${intervalLabel} hasta que la canceles.`}
            </p>
            <Button
              variant="contained"
              color="primary"
              className={classes.primaryButton}
              onClick={() => history.push(HOMEPAGE)}
            >
              Ir al inicio
            </Button>
          </div>
        ) : (
          <>
            <section className={classes.step}>
              <div className={classes.stepTitleRow}>
                {isAuthenticated && (
                  <CheckCircle className={classes.completedIcon} />
                )}
                <h2 className={classes.stepTitle}>Iniciar sesión</h2>
              </div>
              {isAuthenticated ? (
                <p className={classes.stepDescription}>{session.email}</p>
              ) : (
                <>
                  <p className={classes.stepDescription}>
                    Para comprar estos ítems, inicia sesión en tu cuenta o
                    regístrate.
                  </p>
                  {authView === 'buttons' && (
                    <div className={classes.authButtons}>
                      <Button
                        variant="contained"
                        color="primary"
                        className={classes.primaryButton}
                        onClick={() => setAuthView('login')}
                      >
                        Iniciar sesión
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        className={classes.secondaryButton}
                        onClick={() => setAuthView('signup')}
                      >
                        Registrarme
                      </Button>
                    </div>
                  )}
                  {authView === 'login' && (
                    <form className={classes.authForm} onSubmit={handleLogin}>
                      <FormControl
                        className={classes.formControl}
                        error={email === ''}
                      >
                        <InputLabel htmlFor="checkout-email">Email *</InputLabel>
                        <Input
                          id="checkout-email"
                          autoComplete="off"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </FormControl>
                      <FormControl
                        className={classes.formControl}
                        error={password === ''}
                      >
                        <InputLabel htmlFor="checkout-password">
                          Contraseña *
                        </InputLabel>
                        <Input
                          id="checkout-password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                      </FormControl>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        className={classes.primaryButton}
                        disabled={!validateEmail(email) || !password}
                      >
                        Ingresar
                      </Button>
                      <div>
                        <Link
                          className={classes.backLink}
                          onClick={() => setAuthView('signup')}
                        >
                          ¿No tienes cuenta? Regístrate
                        </Link>
                      </div>
                      <div>
                        <Link
                          className={classes.backLink}
                          onClick={() => setAuthView('buttons')}
                        >
                          Volver
                        </Link>
                      </div>
                    </form>
                  )}
                  {authView === 'signup' && (
                    <div className={classes.authForm}>
                      <PatientForm
                        classes={classes}
                        countries={countries}
                        validateEmail={validateEmail}
                        isOlderThan18={isOlderThan18}
                        onSignup={onSignup}
                      />
                      <Link
                        className={classes.backLink}
                        onClick={() => setAuthView('buttons')}
                      >
                        Volver
                      </Link>
                    </div>
                  )}
                </>
              )}
            </section>

            {isAuthenticated ? (
              <>
                <section className={classes.step}>
                  <h2 className={classes.stepTitle}>Detalles del cliente</h2>
                  <div className={classes.customerBox}>
                    <span className={classes.customerName}>{session.name}</span>
                    <span className={classes.customerEmail}>
                      {session.email}
                    </span>
                  </div>
                </section>
                <section className={classes.step}>
                  <h2 className={classes.stepTitle}>Pago</h2>
                  <p className={classes.stepDescription}>
                    Completa el pago para activar tu plan.
                  </p>
                  <StripePaymentForm
                    amountLabel={totalLabel}
                    disabled={!isAuthenticated}
                    loading={checkoutLoading}
                    error={checkoutError}
                    onPay={startCheckoutSession}
                    classes={classes}
                  />
                </section>
              </>
            ) : (
              <>
                <h2 className={classes.stepTitleInactive}>
                  Detalles del cliente
                </h2>
                <h2 className={classes.stepTitleInactive}>Pago</h2>
              </>
            )}
          </>
        )}
      </div>

      {selectedPlan && (
        <aside className={classes.rightColumn}>
          <h2 className={classes.summaryTitle}>Resumen del pedido (1 ítem)</h2>
          <div className={classes.productRow}>
            <div className={classes.productInitials}>
              {getPlanInitials(selectedPlan.productName)}
            </div>
            <div className={classes.productInfo}>
              <div className={classes.productNameRow}>
                <p className={classes.productName}>{selectedPlan.productName}</p>
                <p className={classes.productPrice}>
                  {formatMoney(selectedPlan.amount, selectedPlan.currency)}
                  {intervalLabel && (
                    <span className={classes.productInterval}>
                      {intervalLabel}
                    </span>
                  )}
                </p>
              </div>
              <p className={classes.productDescription}>
                {selectedPlan.productDescription}
              </p>
              <Link className={classes.backLink} onClick={clearPlan}>
                Cambiar plan
              </Link>
            </div>
          </div>

          <div className={classes.totals}>
            <div className={classes.totalRow}>
              <span>Subtotal</span>
              <span>
                {formatMoney(selectedPlan.amount, selectedPlan.currency)}
              </span>
            </div>
            <div className={classes.totalRow}>
              <span>IVA</span>
              <span>{formatMoney(0, selectedPlan.currency)}</span>
            </div>
            <div className={classes.totalFinal}>
              <span>Total</span>
              <span>{totalLabel}</span>
            </div>
          </div>
          <p className={classes.footerNote}>
            Se te cobrarán {totalLabel} cada{' '}
            {getRecurringLabel(
              selectedPlan.interval,
              selectedPlan.intervalCount
            )}{' '}
            hasta que se cancele. Los códigos de promoción se ingresan en
            Stripe.
          </p>
        </aside>
      )}
    </div>
  );

  return (
    <>
      <Header style={{ position: 'relative', boxShadow: '0 0 0 0' }} />
      {userState.success && (
        <Notification
          type="success"
          message={userState.success}
          onCloseCallback={deleteMessages}
        />
      )}
      {userState.error && (
        <Notification
          type="error"
          message={userState.error}
          onCloseCallback={deleteMessages}
        />
      )}
      {cancelNotice && (
        <Notification
          type="warning"
          message="El pago fue cancelado. Podés intentarlo de nuevo."
          onCloseCallback={() => setCancelNotice(false)}
        />
      )}
      {(userState.loading || loading) && <LinearLoader />}
      <div className={classes.page}>
        {!loading && error && plans.length === 0 && (
          <div className={classes.emptyState}>
            <h2 className={classes.successTitle}>No pudimos cargar los planes</h2>
            <p className={classes.successText}>{error}</p>
            <Button
              variant="contained"
              color="primary"
              className={classes.primaryButton}
              onClick={refetchPlans}
            >
              Reintentar
            </Button>
          </div>
        )}

        {!loading && !error && mode === CHECKOUT_MODE.SELECT && (
          <PlanSelector
            plans={plans}
            onSelect={selectPlan}
            classes={classes}
          />
        )}

        {!loading && mode === CHECKOUT_MODE.CHECKOUT && !selectedPlan && (
          <div className={classes.emptyState}>
            <h2 className={classes.successTitle}>Plan no encontrado</h2>
            <p className={classes.successText}>
              El plan indicado en la URL no está disponible. Elige otro para
              continuar.
            </p>
            <Button
              variant="contained"
              color="primary"
              className={classes.primaryButton}
              onClick={clearPlan}
            >
              Ver planes
            </Button>
          </div>
        )}

        {!loading && mode === CHECKOUT_MODE.CHECKOUT && selectedPlan &&
          renderCheckoutForm()}
      </div>
    </>
  );
}

const mapStateToProps = ({ user, session }) => {
  return {
    userState: user,
    session,
  };
};

const mapDispatchToProps = (dispatch) => ({
  onLogin: (credentials) =>
    dispatch({ type: userActionsTypes.USER_LOGIN, payload: credentials }),
  getInitialData: () => dispatch({ type: userActionsTypes.GET_SIGNUP_DATA }),
  onSignup: (type, body) =>
    dispatch({ type: userActionsTypes.USER_SIGNUP, payload: { type, body } }),
  deleteMessages: () =>
    dispatch({
      type: userActionsTypes.DELETE_MESSAGES,
    }),
  getMySubscription: () =>
    dispatch({ type: userActionsTypes.GET_MY_SUBSCRIPTION }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Checkout);
